import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, FileText, CheckSquare, Box, Building } from 'lucide-react';
import { apiCall } from '../../utils/api';

interface SearchResult {
  deployments: any[];
  tasks: any[];
  modules: any[];
  institutes: any[];
}

// What a clicked result points at — App turns this into a view switch.
// (Results used to be <a href="#/deployments/:id"> links that nothing in the
// app handled, so clicking one did nothing.)
export interface SearchNavigateTarget {
  type: 'deployment' | 'task' | 'module' | 'institute';
  id: string;
  title?: string;
}

interface GlobalSearchProps {
  onNavigate?: (target: SearchNavigateTarget) => void;
}

const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : []);

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  // Ignore responses for a query the user has already typed past
  const latestQueryRef = useRef('');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    latestQueryRef.current = query;
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        performSearch(query);
      } else {
        setResults(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const performSearch = async (q: string) => {
    try {
      setLoading(true);
      const response = await apiCall(`/api/search?q=${encodeURIComponent(q)}&limit=5`);
      const data = await response.json();
      if (latestQueryRef.current !== q) return;
      // Normalise — a missing category used to crash getTotalResults
      setResults({
        deployments: asArray(data?.deployments),
        tasks: asArray(data?.tasks),
        modules: asArray(data?.modules),
        institutes: asArray(data?.institutes),
      });
      setIsOpen(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      if (latestQueryRef.current === q) setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
  };

  const go = (target: SearchNavigateTarget) => {
    setIsOpen(false);
    onNavigate?.(target);
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return (
      results.deployments.length +
      results.tasks.length +
      results.modules.length +
      results.institutes.length
    );
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search deployments, tasks, modules, institutes..."
          className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          onFocus={() => query.length >= 2 && setIsOpen(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 animate-spin" />
        )}
        {query && !loading && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {isOpen && results && getTotalResults() > 0 && (
        <div className="absolute top-full mt-2 w-full min-w-[18rem] bg-white border border-gray-200 rounded-lg shadow-xl max-h-[500px] overflow-y-auto z-50">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500">
              Found {getTotalResults()} result{getTotalResults() !== 1 ? 's' : ''}
            </p>
          </div>

          {results.deployments.length > 0 && (
            <div className="p-2">
              <div className="flex items-center gap-2 px-2 py-1 mb-1">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-700">Deployments</span>
              </div>
              {results.deployments.map((deployment) => (
                <button
                  type="button"
                  key={deployment._id}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md"
                  onClick={() => go({ type: 'deployment', id: String(deployment._id), title: deployment.title })}
                >
                  <div className="flex items-center gap-2">
                    {deployment.deploymentNumber != null && (
                      <span className="font-mono text-sm font-semibold">#{deployment.deploymentNumber}</span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      deployment.status === 'deployed' ? 'bg-green-100 text-green-700' :
                      deployment.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {deployment.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 mt-0.5">{deployment.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {deployment.requestedBy?.name}
                    {deployment.scheduledDate ? ` • ${new Date(deployment.scheduledDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}` : ''}
                  </p>
                </button>
              ))}
            </div>
          )}

          {results.tasks.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="flex items-center gap-2 px-2 py-1 mb-1">
                <CheckSquare className="h-4 w-4 text-purple-600" />
                <span className="text-xs font-semibold text-gray-700">Tasks</span>
              </div>
              {results.tasks.map((task) => (
                <button
                  type="button"
                  key={task._id}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md"
                  onClick={() => go({ type: 'task', id: String(task._id), title: task.title })}
                >
                  <div className="flex items-center gap-2">
                    {task.taskNumber != null && (
                      <span className="font-mono text-sm font-semibold">#{task.taskNumber}</span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.status}
                    </span>
                    {task.priority && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        task.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {task.priority}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-900 mt-0.5">{task.title}</p>
                </button>
              ))}
            </div>
          )}

          {results.modules.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="flex items-center gap-2 px-2 py-1 mb-1">
                <Box className="h-4 w-4 text-green-600" />
                <span className="text-xs font-semibold text-gray-700">Modules</span>
              </div>
              {results.modules.map((module) => (
                <button
                  type="button"
                  key={module._id}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md"
                  onClick={() => go({ type: 'module', id: String(module._id), title: module.name })}
                >
                  <p className="text-sm font-medium text-gray-900">{module.name}</p>
                  {module.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{module.description}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {results.institutes.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="flex items-center gap-2 px-2 py-1 mb-1">
                <Building className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-semibold text-gray-700">Institutes</span>
              </div>
              {results.institutes.map((institute) => (
                <button
                  type="button"
                  key={institute._id}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md"
                  onClick={() => go({ type: 'institute', id: String(institute._id), title: institute.name })}
                >
                  <p className="text-sm font-medium text-gray-900">{institute.name}</p>
                  {institute.domain && (
                    <p className="text-xs text-gray-500 mt-0.5">{institute.domain}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isOpen && results && getTotalResults() === 0 && query.length >= 2 && !loading && (
        <div className="absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl p-6 text-center z-50">
          <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No results found for "{query}"</p>
          <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
        </div>
      )}
    </div>
  );
};
